import { BlockStack, Box, Text, Card, Icon, InlineStack, Page, Badge, Button, Divider, DescriptionList, DataTable, Link } from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CashDollarIcon, InfoIcon, StatusActiveIcon, TaxFilledIcon, ProductUnavailableIcon, DeleteIcon, PageDownIcon } from '@shopify/polaris-icons';
import { Helmet } from 'react-helmet-async';
import __helpers from 'helpers/index';
import DriverSettlementFilter from './filter';
import { useLocation, useNavigate } from 'react-router-dom';
import Pagination from 'components/pagination';
import {
  TypedDriver_settlement,
  useDownloadCurrentDriverSettlement,
  useGetDriver_settlementAnalytics,
  useGetDriver_settlements,
  useMutateDriverSettlementDelete,
  useMutateDriverSettlementMarkAsClose,
  useMutateDriverSettlementMarkAsOpen,
} from 'queries/driver_settlement.query';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useDeleteConfirm } from 'context/DeleteConfirmContext';
import Capacity from 'components/Capacity';
import ExpandableDataTable from 'components/ExpandableDataTable';
import { useNotification } from 'context/NotificationContext';
import { ORDER_FEE_LABEL, TRIP_FEE_LABELS } from 'queries/orders.query';
dayjs.extend(utc);
dayjs.extend(timezone);

export default function DriverSettlementList() {
  const { data: analyticsData } = useGetDriver_settlementAnalytics();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 800px)');
    const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
      const matches = 'matches' in event ? event.matches : mediaQuery.matches;
      setIsMobile(matches);
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  const navigate = useNavigate();

  const [entities, setEntities] = useState<TypedDriver_settlement[]>([]);
  const [totalItems, setTotalItems] = useState(0);

  /**
   * If user apply filter, it will add to URL, then parse URL back to initial state
   */
  let { search } = useLocation();
  let StringQuery: any = __helpers.ExtractUrl(search);

  const initialQuery = {
    query: '',
    page: 1,
    limit: 100,
    sort: 'createdAt:desc',
  };
  const [mainQuery, setMainQuery] = useState({
    ...initialQuery,
    ...StringQuery,
  });

  const { data, refetch: getEntities, isPending: loading } = useGetDriver_settlements(mainQuery);

  useEffect(() => {
    if (data) {
      setEntities(data.body);
      setTotalItems(data.totalItems);
    }
  }, [data]);

  /**
   * Change page number
   */
  const onChangePagination = useCallback(
    (numPage: number, limit: number) => {
      setMainQuery({ ...mainQuery, page: numPage, limit: limit });
    },
    [mainQuery]
  );

  const reduceRequest = useCallback((mainQuery) => {
    getEntities();
    return mainQuery;
  }, []);

  const reduceRequestMemo = useMemo(() => {
    return __helpers.debounce((_value) => {
      reduceRequest?.call(this, _value);
    }, 500);
  }, []);

  /**
   * I do not know ...
   * Bug: In React, do NOT remove this code
   */
  const filterCallback = useCallback((_value: any) => {
    if (_value === false) {
      return setMainQuery(initialQuery);
    } else {
      setMainQuery((oldValue) => {
        return { ...oldValue, ..._value };
      });
    }
  }, []);

  /**
   * Double request!
   */
  useEffect(() => {
    let buildURLSearch = __helpers.buildEndUrl(mainQuery);
    if (search !== buildURLSearch) navigate(`/driver_settlement` + buildURLSearch);
    reduceRequestMemo(mainQuery);
  }, [mainQuery]);

  /** đánh dấu thu tiền ... */
  const [markingID, setMarkingID] = useState(null);
  const { mutateAsync: markAsDoneAsync, isPending: marking_as_done } = useMutateDriverSettlementMarkAsClose();
  const markAsDoneCallback = useCallback(async (trip_manifest_id) => {
    setMarkingID(trip_manifest_id);
    try {
      await markAsDoneAsync(trip_manifest_id);
    } catch (e) {}
    setMarkingID(null);
  }, []);

  const { showDeleteConfirm } = useDeleteConfirm();
  const { addNotification } = useNotification();

  /** Đánh dấu xóa bỏ tình trạng thanh toán  */
  const [notdoneID, setNotdoneID] = useState(null);
  const { mutateAsync: rolebackPayment, isPending: marking_as_not_done } = useMutateDriverSettlementMarkAsOpen();
  const markAsNotDoneCallback = useCallback(async (trip_manifest_id) => {
    setNotdoneID(trip_manifest_id);
    const confirmed = await showDeleteConfirm('Eii bạn chắc chắn chứ?', 'Phải kiểm tra kỹ khi thay đổi tình trạng của một quyết toán.');
    try {
      if (confirmed) {
        await rolebackPayment(trip_manifest_id);
      }
    } catch (e) {}
    setNotdoneID(null);
  }, []);

  /** Đánh dấu xóa bỏ record luôn */
  const [deletingID, setDeletingID] = useState(null);
  const { mutateAsync: deleteDriverSettlement, isPending: isDeleting } = useMutateDriverSettlementDelete();
  const deleteDriverSettlementCallback = useCallback(async (trip_manifest_id) => {
    setDeletingID(trip_manifest_id);
    const confirmed = await showDeleteConfirm(
      'Eii bạn chắc chắn chứ?',
      'Phải kiểm tra kỹ khi xóa một quyết toán. Đây là hành động không thể hoàn tác.'
    );
    try {
      if (confirmed) {
        await deleteDriverSettlement(trip_manifest_id);
      }
    } catch (e) {}
    setDeletingID(null);
  }, []);

  /** Tính tổng trong bảng  */
  let [totalInTable, setTotalInTable] = useState({
    total_settlement: 0,
    total_fee: 0,
    total_fare: 0,
    total_due: 0,
    total_settlement_amount: 0,
    total_driver_earning_amount: 0,
  });

  const formatMoney = useCallback((value?: number | string | null) => {
    return `${__helpers.formatNumberWithCommas(Number(value || 0))} vnd`;
  }, []);

  const getFeeLabel = useCallback((feeKey: string, type: 'order' | 'trip') => {
    const labels = type === 'order' ? ORDER_FEE_LABEL : TRIP_FEE_LABELS;
    return labels[feeKey as keyof typeof labels] || feeKey;
  }, []);

  const renderEmptyDescription = useCallback((message = 'Không có dữ liệu') => {
    return [{ term: '--', description: message }];
  }, []);

  useEffect(() => {
    if (!entities) return;

    let total = {
      total_settlement: 0,
      total_fee: 0,
      total_fare: 0,
      total_due: 0,
      total_settlement_amount: 0,
      total_driver_earning_amount: 0,
    };

    for (let driverSettlement of entities) {
      total.total_settlement += 1;
      total.total_fee += Number(driverSettlement.total_fee);
      total.total_fare += Number(driverSettlement.total_fare);
      total.total_due += Number(driverSettlement.total_due);
      total.total_settlement_amount += Number(driverSettlement.total_settlement_amount);
      total.total_driver_earning_amount += Number(driverSettlement.driver_earning_amount);
    }

    setTotalInTable(total);
  }, [entities]);

  /** Xuất excel */
  const { mutateAsync: downloadExcelThisFilter, isPending: downloadGenerating } = useDownloadCurrentDriverSettlement();
  const generateDownloadLink = useCallback(async () => {
    try {
      const blob = await downloadExcelThisFilter(mainQuery);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = `driver_settlement-${dayjs().format('YYYY-MM-DD')}.xlsx`;

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (e) {}
  }, [mainQuery]);

  return (
    <>
      <Helmet>
        <title>Quyết toán</title>
      </Helmet>
      <Page
        fullWidth
        title="Quyết toán"
        primaryAction={{
          icon: PageDownIcon,
          content: 'Tải xuống bảng này',
          onAction: () => generateDownloadLink(),
        }}
      >
        <InlineStack align="start" blockAlign="start" gap="400">
          <BlockStack gap="200">
            <Text as="h2" variant="headingMd" fontWeight="bold">
              Thống kê chung
            </Text>
            <InlineStack gap={isMobile ? '200' : '400'} wrap>
              <Box minWidth={isMobile ? '100%' : '160px'} width={isMobile ? '100%' : undefined}>
                <Card padding="300">
                  <BlockStack gap="100">
                    <InlineStack gap="200" blockAlign="center" align="start" wrap={false}>
                      <Box as="span">
                        <Icon source={InfoIcon} tone="subdued" />
                      </Box>
                      <Text as="span" tone="subdued">
                        Tổng số dữ liệu
                      </Text>
                    </InlineStack>
                    <Text variant="headingLg" as="p">
                      {__helpers.formatNumberWithCommas(analyticsData?.total_settlements || 0)} issues
                    </Text>
                  </BlockStack>
                </Card>
              </Box>
              <Box minWidth={isMobile ? '100%' : '160px'} width={isMobile ? '100%' : undefined}>
                <Card padding="300">
                  <BlockStack gap="100">
                    <InlineStack gap="200" blockAlign="center" align="start" wrap={false}>
                      <Box as="span">
                        <Icon source={TaxFilledIcon} tone="subdued" />
                      </Box>
                      <Text as="span" tone="subdued">
                        Đang mở
                      </Text>
                    </InlineStack>
                    <Text variant="headingLg" as="p">
                      {__helpers.formatNumberWithCommas(analyticsData?.total_open_settlements || 0)} issues
                    </Text>
                  </BlockStack>
                </Card>
              </Box>
              <Box minWidth={isMobile ? '100%' : '160px'} width={isMobile ? '100%' : undefined}>
                <Card padding="300">
                  <BlockStack gap="100">
                    <InlineStack gap="200" blockAlign="center" align="start" wrap={false}>
                      <Box as="span">
                        <Icon source={CashDollarIcon} tone="subdued" />
                      </Box>
                      <Text as="span" tone="subdued">
                        Đã đóng
                      </Text>
                    </InlineStack>
                    <Text variant="headingLg" as="p">
                      {__helpers.formatNumberWithCommas(analyticsData?.total_closed_settlements || 0)} issues
                    </Text>
                  </BlockStack>
                </Card>
              </Box>
            </InlineStack>
          </BlockStack>

          <BlockStack gap="200">
            <Text as="h2" variant="headingMd" fontWeight="bold">
              Trong bảng này
            </Text>
            <InlineStack gap={isMobile ? '200' : '400'} wrap>
              <Box minWidth={isMobile ? '100%' : '160px'} width={isMobile ? '100%' : undefined}>
                <Card padding="300" background="bg-surface-info-hover">
                  <BlockStack gap="100">
                    <InlineStack gap="200" blockAlign="center" align="start" wrap={false}>
                      <Box as="span">
                        <Icon source={InfoIcon} tone="subdued" />
                      </Box>
                      <Text as="span" tone="subdued">
                        Tổng quyết toán
                      </Text>
                    </InlineStack>
                    <Text variant="headingLg" as="p">
                      {data?.analytics?.total?.amount ? __helpers.formatNumberWithCommas(data.analytics.total.amount) : '0'} vnd
                    </Text>
                    <Text as="p" variant="bodyMd" tone="subdued">
                      {data?.analytics?.total?.count || 0} issues
                    </Text>
                  </BlockStack>
                </Card>
              </Box>
              <Box minWidth={isMobile ? '100%' : '160px'} width={isMobile ? '100%' : undefined}>
                <Card padding="300" background="bg-surface-info-hover">
                  <BlockStack gap="100">
                    <InlineStack gap="200" blockAlign="center" align="start" wrap={false}>
                      <Box as="span">
                        <Icon source={InfoIcon} tone="subdued" />
                      </Box>
                      <Text as="span" tone="subdued">
                        Đang mở
                      </Text>
                    </InlineStack>
                    <Text variant="headingLg" as="p">
                      {data?.analytics?.open?.amount ? __helpers.formatNumberWithCommas(data.analytics.open.amount) : '0'} vnd
                    </Text>
                    <Text as="p" variant="bodyMd" tone="subdued">
                      {data?.analytics?.open?.count || 0} issues{' '}
                    </Text>
                  </BlockStack>
                </Card>
              </Box>
              <Box minWidth={isMobile ? '100%' : '160px'} width={isMobile ? '100%' : undefined}>
                <Card padding="300" background="bg-surface-info-hover">
                  <BlockStack gap="100">
                    <InlineStack gap="200" blockAlign="center" align="start" wrap={false}>
                      <Box as="span">
                        <Icon source={InfoIcon} tone="subdued" />
                      </Box>
                      <Text as="span" tone="subdued">
                        Đã đóng
                      </Text>
                    </InlineStack>
                    <Text variant="headingLg" as="p">
                      {data?.analytics?.closed?.amount ? __helpers.formatNumberWithCommas(data.analytics.closed.amount) : '0'} vnd
                    </Text>
                    <Text as="p" variant="bodyMd" tone="subdued">
                      {data?.analytics?.closed?.count || 0} issues
                    </Text>
                  </BlockStack>
                </Card>
              </Box>
            </InlineStack>
          </BlockStack>
        </InlineStack>

        <br />
        <Card padding="0">
          <DriverSettlementFilter onCallback={filterCallback} loading={loading} />
          <DataTable
            headings={[
              'ID',
              'Tình trạng',
              'Tài xế',
              'Tổng Q.toán',
              'Chuyến',
              'Ngày đi',
              'Tổng vé',
              'Tổng phí',
              'Tài xế thu',
              'Lương',
              'Ngày tạo phiếu',
              'Người thu tiền',
              'Ngày thu tiền',
              'C.cụ',
            ]}
            columnContentTypes={['text', 'text', 'text', 'text']}
            verticalAlign="middle"
            hideScrollIndicator
            hasZebraStripingOnData
            increasedTableDensity
            // columnWidths={[
            //   '60px',
            //   '100px',
            //   '200px',
            //   '110px',
            //   '200px',
            //   '110px',
            //   '110px',
            //   '110px',
            //   '110px',
            //   '100px',
            //   '110px',
            //   '110px',
            //   '110px',
            //   '110px',
            // ]}
            // minWidth={1000}
            // rows={entities}
            rows={
              entities?.map((item) => [
                <Link onClick={() => navigate(`/driver_settlement/${item.id}`)}>{item.id}</Link>,
                <Badge tone={item.driver_settlement_status === 'CLOSED' ? 'success' : 'critical'}>{item.driver_settlement_status}</Badge>,
                <strong>
                  {item.driver_data?.display_name} / {item.driver_data?.user_phonenumber}
                </strong>,
                item.total_settlement_amount ? __helpers.formatNumberWithCommas(item.total_settlement_amount) : '0',

                <Link
                  url={`/ticket_managers?d=${dayjs(Number(item.trip_manifest?.departure_date)).tz('Asia/Ho_Chi_Minh').format('YYYY-MM-DD')}&journey_id=${item?.journey_id}&h=${item?.trip_manifest?.time_id}`}
                >
                  <strong>{`${item.trip_name} (${item.license_plate || ''})`}</strong>
                </Link>,
                item.trip_departure_date ? dayjs(Number(item.trip_departure_date)).tz('Asia/Ho_Chi_Minh').format('HH:mm DD-MM-YYYY') : '',
                item.total_fare ? __helpers.formatNumberWithCommas(item.total_fare) : '0',
                item.total_fee ? __helpers.formatNumberWithCommas(item.total_fee) : '0',
                item.total_due ? __helpers.formatNumberWithCommas(item.total_due) : '0',
                item.driver_earning_amount ? __helpers.formatNumberWithCommas(item.driver_earning_amount) : '0',
                item.createdAt ? dayjs(Number(item.createdAt)).tz('Asia/Ho_Chi_Minh').format('DD-MM-YYYY') : '',
                item.money_collector?.display_name || '--',
                item.money_collected_at ? dayjs(Number(item.money_collected_at)).tz('Asia/Ho_Chi_Minh').format('HH:mm DD-MM-YYYY') : '--',
                item.money_collected_at ? (
                  <span
                    onClick={(event) => event.stopPropagation()}
                    onMouseDown={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <Button
                      variant="tertiary"
                      tone="critical"
                      icon={ProductUnavailableIcon}
                      loading={marking_as_not_done && notdoneID === item.id}
                      onClick={() => markAsNotDoneCallback(item.id)}
                      onKeyDown={(event) => event.stopPropagation()}
                      onKeyPress={(event) => event.stopPropagation()}
                    >
                      Mở lại
                    </Button>
                  </span>
                ) : (
                  <span
                    onClick={(event) => event.stopPropagation()}
                    onMouseDown={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <Button
                      variant="tertiary"
                      tone="success"
                      icon={StatusActiveIcon}
                      loading={marking_as_done && markingID === item.id}
                      onClick={() => markAsDoneCallback(item.id)}
                      onKeyDown={(event) => event.stopPropagation()}
                      onKeyPress={(event) => event.stopPropagation()}
                    >
                      Thu tiền
                    </Button>
                  </span>
                ),
              ]) || []
            }
            // renderExpanded={(item, index) => {
            //   return (
            //     <BlockStack gap="400">
            //       <InlineStack align="space-between" blockAlign="start" gap="400">
            //         <BlockStack gap="400">
            //           <Text as="p">Details</Text>
            //         </BlockStack>
            //         <InlineStack align="end" blockAlign="center" gap="400">
            //           {item.money_collected_at ? (
            //             <Button
            //               variant="monochromePlain"
            //               icon={ProductUnavailableIcon}
            //               loading={marking_as_not_done && notdoneID === item.id}
            //               onClick={() => markAsNotDoneCallback(item.id)}
            //             >
            //               Mở lại
            //             </Button>
            //           ) : (
            //             <Button
            //               variant="monochromePlain"
            //               icon={StatusActiveIcon}
            //               loading={marking_as_done && markingID === item.id}
            //               onClick={() => markAsDoneCallback(item.id)}
            //             >
            //               Thu tiền
            //             </Button>
            //           )}

            //           <Capacity current_user_can="super_admin" onFail={null}>
            //             <Button
            //               variant="monochromePlain"
            //               loading={isDeleting && deletingID === item.id}
            //               icon={DeleteIcon}
            //               onClick={() => deleteDriverSettlementCallback(item.id)}
            //             >
            //               Xóa quyết toán
            //             </Button>
            //           </Capacity>
            //         </InlineStack>
            //       </InlineStack>

            //       <InlineStack align="start" blockAlign="start" gap="400">
            //         <BlockStack gap="400">
            //           <Text as="h3" variant="headingLg" fontWeight="bold">
            //             Vé
            //           </Text>
            //           <DescriptionList
            //             gap="tight"
            //             items={
            //               item.order_snapshot && Object.keys(item.order_snapshot).length
            //                 ? Object.entries(item.order_snapshot).map(([orderCode, order]) => ({
            //                     term: orderCode,
            //                     description: (
            //                       <BlockStack gap="100">
            //                         <Text as="span">Tiền vé: {formatMoney(order.total_must_pay)}</Text>
            //                         <Text as="span">Tổng phí: {formatMoney(order.total_fee)}</Text>
            //                         <Text as="span">Đón: {order.pickup_address || '--'}</Text>
            //                         <Text as="span">Trả: {order.dropoff_address || '--'}</Text>
            //                       </BlockStack>
            //                     ),
            //                   }))
            //                 : renderEmptyDescription()
            //             }
            //           />
            //           <Text as="h3" variant="headingLg" fontWeight="bold">
            //             Phí vé
            //           </Text>
            //           <DescriptionList
            //             gap="tight"
            //             items={
            //               item.order_fee_snapshot && Object.keys(item.order_fee_snapshot).length
            //                 ? Object.entries(item.order_fee_snapshot).flatMap(([orderCode, fees]) =>
            //                     Object.entries(fees).map(([feeKey, feeValue]) => ({
            //                       term: `${orderCode} - ${getFeeLabel(feeKey, 'order')}`,
            //                       description: formatMoney(feeValue),
            //                     }))
            //                   )
            //                 : renderEmptyDescription()
            //             }
            //           />
            //           <Text as="h3" variant="headingLg" fontWeight="bold">
            //             Phí chuyến
            //           </Text>
            //           <DescriptionList
            //             gap="tight"
            //             items={
            //               item.trip_manifest_fee_snapshot && Object.keys(item.trip_manifest_fee_snapshot).length > 0
            //                 ? Object.entries(item.trip_manifest_fee_snapshot).map(([feeKey, feeValue], index) => ({
            //                     term: `${index + 1}. ${getFeeLabel(feeKey, 'trip')}`,
            //                     description: formatMoney(feeValue),
            //                   }))
            //                 : renderEmptyDescription()
            //             }
            //           />

            //           <Text as="h3" variant="headingLg" fontWeight="bold">
            //             Tài chính
            //           </Text>
            //           <DescriptionList
            //             gap="tight"
            //             items={[
            //               { term: 'Tài xế thu', description: formatMoney(item.total_due) },
            //               { term: 'Tổng phí', description: formatMoney(item.total_fee) },
            //               { term: 'Tổng quyết toán', description: formatMoney(item.total_settlement_amount) },
            //               { term: 'Lương/thưởng tài xế', description: formatMoney(item.driver_earning_amount) },
            //               { term: 'Giải trình lương', description: item.driver_earning_snapshot?.calculation?.formula || '' },
            //             ]}
            //           />
            //           <Text as="h3" variant="headingLg" fontWeight="bold">
            //             Thông tin chuyến
            //           </Text>
            //           <DescriptionList
            //             gap="tight"
            //             items={[
            //               {
            //                 term: 'Tài xế',
            //                 description: `${item.driver_data?.display_name || '--'} / ${item.driver_data?.user_phonenumber || '--'}`,
            //               },
            //               { term: 'Hành trình', description: item.trip_name || '--' },
            //               { term: 'Xe', description: item.license_plate || '--' },
            //               {
            //                 term: 'Ngày giờ đi',
            //                 description: item.trip_departure_date
            //                   ? `${dayjs(Number(item.trip_departure_date)).tz('Asia/Ho_Chi_Minh').format('DD-MM-YYYY')} ${item.trip_manifest?.departure_hour || ''}`
            //                   : '--',
            //               },
            //               { term: 'Số ghế / hàng', description: `${item.total_seats || 0} ghế / ${item.total_goods || 0} hàng` },
            //               { term: 'Tổng tiền vé', description: formatMoney(item.total_fare) },
            //             ]}
            //           />
            //         </BlockStack>
            //       </InlineStack>
            //     </BlockStack>
            //   );
            // }}
            totals={[
              'Tổng',
              `${totalInTable.total_settlement} chuyến`,
              '-',
              `${__helpers.formatNumberWithCommas(totalInTable.total_settlement_amount)}`,
              '-',
              '-',
              `${__helpers.formatNumberWithCommas(totalInTable.total_fare)}`,
              `${__helpers.formatNumberWithCommas(totalInTable.total_fee)}`,
              `${__helpers.formatNumberWithCommas(totalInTable.total_due)}`,
              `${__helpers.formatNumberWithCommas(totalInTable.total_driver_earning_amount)}`,
              '-',
              '-',
              '-',
              '-',
            ]}
            showTotalsInFooter
          />
        </Card>

        {totalItems > mainQuery.limit ? (
          <Pagination TotalRecord={totalItems} activeCurrentPage={mainQuery.page} pageSize={mainQuery.limit} onChange={onChangePagination} />
        ) : null}
      </Page>

      <br />
      <br />
      <br />
      <br />
      <br />
    </>
  );
}

